import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import {
  ILoginRequest,
  IRefreshTokenRequest,
} from "../interfaces/login.interface";
import { IUsuario } from "@/interfaces/usuarios.interface";
import { z } from "zod";
import { loginSchema } from "@/pages/NewUser";
import { getApiBaseUrl } from "@/lib/rtk-query-config";

const authBaseUrl = `${getApiBaseUrl()}/auth`;

const authBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const { baseQueryWithReauth } = await import("@/lib/api");
  const fetchArgs: FetchArgs =
    typeof args === "string" ? { url: args } : { ...args };
  const relativeUrl =
    typeof fetchArgs.url === "string"
      ? fetchArgs.url.replace(/^\//, "")
      : fetchArgs.url;
  const url =
    typeof relativeUrl === "string" && relativeUrl.startsWith("auth/")
      ? `/${relativeUrl}`
      : `/auth/${relativeUrl}`;
  return baseQueryWithReauth({ ...fetchArgs, url }, api, extraOptions);
};

export async function getGoogleAuthUrl(
  loginId?: number
): Promise<{ url: string }> {
  const params = loginId ? `?loginId=${loginId}` : "";
  const res = await fetch(`${authBaseUrl}/google/url${params}`);
  if (!res.ok) {
    throw new Error("Erro ao obter URL de autenticação Google");
  }
  return res.json();
}

export async function exchangeGoogleCode(
  code: string,
  state?: string,
  session_kind?: "pwa"
): Promise<IUsuario> {
  const res = await fetch(`${authBaseUrl}/google/exchange`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, state: state || undefined, session_kind }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { message?: string })?.message ||
      "Erro ao trocar código por tokens"
    );
  }
  return res.json();
}

/**
 * Troca o JWT (id_token/credential) do Google Identity Services pelo usuário e tokens da aplicação.
 * Backend: POST /auth/google/credential
 * Body: { credential: string, loginId?: number }
 * Resposta: mesmo formato do login (access_token, refresh_token, email, name, id, fotoId, logins, ...)
 */
export async function exchangeGoogleCredential(
  credential: string,
  loginId?: number,
  session_kind?: "pwa"
): Promise<IUsuario> {
  const res = await fetch(`${authBaseUrl}/google/credential`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      credential,
      loginId: loginId ?? undefined,
      session_kind,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { message?: string })?.message ||
      "Erro ao autenticar com Google"
    );
  }
  return res.json();
}

/**
 * Valida token SSO recebido após login em domínio central.
 * Backend: POST /auth/sso/validate
 * Body: { token: string }
 * Resposta: IUsuario (mesmo formato do login)
 */
export async function validateSsoToken(token: string, session_kind?: "pwa"): Promise<IUsuario> {
  const res = await fetch(`${authBaseUrl}/sso/validate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, session_kind }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { message?: string })?.message || "Token SSO inválido ou expirado"
    );
  }
  return res.json();
}

export const authAPI = createApi({
  reducerPath: "authAPI",
  baseQuery: authBaseQuery,
  tagTypes: ["Auth"],
  endpoints: (build) => ({
    login: build.mutation<any, ILoginRequest>({
      query: ({ email, password, session_kind }) => ({
        url: `login`,
        method: "POST",
        body: { email, password, ...(session_kind ? { session_kind } : {}) },
      }),
    }),
    recuperar: build.mutation<any, string>({
      query: (email) => ({
        url: `recuperar-senha`,
        method: "POST",
        body: { email },
      }),
    }),
    newUser: build.mutation<any, z.infer<typeof loginSchema>>({
      query: (body) => ({
        url: `newUser`,
        method: "POST",
        body,
      }),
    }),
    refreshToken: build.mutation<any, IRefreshTokenRequest>({
      query: ({ refresh_token }) => ({
        url: `refresh`,
        method: "POST",
        body: { refresh_token },
      }),
    }),
    getUserById: build.query<any, string>({
      query: (id) => ({
        url: `newUser/${id}`,
        method: "GET",
      }),
    }),
    getLoginLojinha: build.query<
      {
        Id: number;
        ImprimirComprovanteAuto?: boolean;
        PermitirSaldoNegativo?: boolean;
        LimiteSaldoNegativoPadrao?: number | string | null;
      },
      { loginId: number }
    >({
      query: ({ loginId }) => ({
        url: `login/lojinha`,
        method: "GET",
        params: { loginId },
      }),
      providesTags: (_result, _error, { loginId }) => [
        { type: "Auth", id: `login-lojinha-${loginId}` },
      ],
    }),
    updateLoginLojinha: build.mutation<
      {
        Id: number;
        ImprimirComprovanteAuto?: boolean;
        PermitirSaldoNegativo?: boolean;
        LimiteSaldoNegativoPadrao?: number;
      },
      {
        loginId: number;
        PermitirSaldoNegativo?: boolean;
        LimiteSaldoNegativoPadrao?: number;
        ImprimirComprovanteAuto?: boolean;
      }
    >({
      query: ({ loginId, ...body }) => ({
        url: `login/lojinha`,
        method: "PUT",
        params: { loginId },
        body,
      }),
      invalidatesTags: (_result, _error, { loginId }) => [
        { type: "Auth", id: `login-lojinha-${loginId}` },
      ],
    }),
      
    registerSendCode: build.mutation<{ sent: boolean; expiresInMinutes: number; sessionToken: string }, { email: string; loginId: number }>({
      query: (body) => ({ url: `register/send-code`, method: "POST", body }),
    }),
    registerVerifyCode: build.mutation<{ verified: boolean; sessionToken: string; email: string; loginId: number }, { email?: string; loginId?: number; code?: string; token?: string }>({
      query: (body) => ({ url: `register/verify-code`, method: "POST", body }),
    }),
    registerComplete: build.mutation<any, { sessionToken: string; nome: string; senha: string; confirmarSenha: string }>({
      query: (body) => ({ url: `register/complete`, method: "POST", body }),
    }),
    /** Garante usuário na org destino (cria se ainda não existir) e atualiza sessão PWA. */
    switchLogin: build.mutation<
      { ok: boolean; loginId: number; created?: boolean },
      { loginId: number; refresh_token?: string }
    >({
      query: (body) => ({
        url: `switch-login`,
        method: "POST",
        body,
      }),
    }),
    registrarOrigem: build.mutation<
      { ok: boolean; origem: string; contaNova: boolean },
      {
        utm_source: string;
        utm_medium?: string;
        utm_campaign?: string;
        grupoId?: number;
        primeiroToqueEm?: string;
        canal?: "web";
      }
    >({
      query: (body) => ({
        url: `me/origem`,
        method: "POST",
        body,
      }),
    }),
    getProfileCompletion: build.query<
      { incomplete: boolean; missing: string[]; equipanteId: number },
      { loginId: number }
    >({
      query: ({ loginId }) => ({
        url: `me/profile-completion`,
        method: "GET",
        params: { loginId },
      }),
      providesTags: [{ type: "Auth", id: "PROFILE_COMPLETION" }],
    }),
  }),
});

export const {
  useRegistrarOrigemMutation,
  useLoginMutation,
  useRefreshTokenMutation,
  useGetUserByIdQuery,
  useNewUserMutation,
  useRecuperarMutation,
  useGetLoginLojinhaQuery,
  useUpdateLoginLojinhaMutation,
  useRegisterSendCodeMutation,
  useRegisterVerifyCodeMutation,
  useRegisterCompleteMutation,
  useSwitchLoginMutation,
  useGetProfileCompletionQuery,
} = authAPI;
